import type { ReactNode } from 'react';
import { DS2 } from '../../theme/designTokens';

interface Props {
  label:         string;
  value:         string;
  onChange:      (v: string) => void;
  type?:         string;
  autoComplete?: string;
  /** Optional icon/button rendered inside the field, right-aligned (e.g. a password visibility toggle). */
  rightSlot?:    ReactNode;
  /** Inline error attached to THIS field (e.g. "An account already exists
   *  with this email address.") — distinct from a form's generic bottom
   *  error paragraph (Login/ForgotPassword's pattern), used where an error
   *  is specifically about one field's value. Optional/additive: omitting
   *  it renders exactly as before this prop existed. */
  error?:        string;
}

/**
 * DS2 Auth-family field — Figma "Frame 59/60": white box, 1.5px gold
 * border, radius 16 ×0.75 → 12. Extracted from the approved LoginScreen
 * (its Email/Password fields use this with no rightSlot/error, so their
 * rendered output is unchanged by this extraction).
 */
export function AuthField({ label, value, onChange, type = 'text', autoComplete, rightSlot, error }: Props) {
  const id = `auth-field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${id}-error`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: DS2.font.body,
          fontWeight: 400,
          fontSize:   12.6,
          lineHeight: '15px',
          color:      '#FFFFFF',
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          style={{
            boxSizing:    'border-box',
            width:        '100%',
            height:       39,
            padding:      `0 ${rightSlot ? 40 : 16}px 0 16px`,
            borderRadius: 12,
            border:       `1.125px solid ${error ? '#f87171' : '#C9A84C'}`,
            background:   '#FFFFFF',
            color:        '#0B0B0B',
            fontFamily:   DS2.font.body,
            fontSize:     14,
            outline:      'none',
          }}
        />
        {rightSlot && (
          <div style={{ position: 'absolute', right: 12, display: 'flex', alignItems: 'center' }}>
            {rightSlot}
          </div>
        )}
      </div>
      {error && (
        <p id={errorId} role="alert" style={{ margin: 0, fontFamily: DS2.font.body, fontSize: 11.5, color: '#f87171' }}>
          {error}
        </p>
      )}
    </div>
  );
}
